import React, { useState, useMemo } from 'react';
import { CardContent, Stack, Tabs, Tab, Box } from '@mui/material';
import { useAppSelector } from '../../../../app/hooks';
import CategoryClassificationSection from './sections/CategoryClassificationSection';
import TicketInformationSection from './sections/TicketInformationSection';
import AssignmentInformationSection from './sections/AssignmentInformationSection';
import TimeTrackingSection from './sections/TimeTrackingSection';
import CustomerInformationSection from './sections/CustomerInformationSection';
import DocumentsSection from './sections/DocumentsSection';
import NotesSection from './sections/NotesSection';
import ResolutionSection from './sections/ResolutionSection';
import WorkflowApprovalsSection from './sections/WorkflowApprovalsSection';
import { HstAccessSection } from './sections/HstAccessSection';
import { CpsiAccessSection } from './sections/CpsiAccessSection';
import { FacilityMaintenanceSection } from './sections/FacilityMaintenanceSection';
import { PMDetailsSection } from './sections/PMDetailsSection';
import { CostSection } from './sections/CostSection';
import { CustomApplicationSection } from './sections/CustomApplicationSection';
import { ReportingSection } from './sections/ReportingSection';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

/**
 * TabPanel - Wrapper component for tab content
 */
const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`ticket-form-tabpanel-${index}`}
      aria-labelledby={`ticket-form-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

/**
 * TicketEditForm - Main form container for ticket editing
 * Organizes sections with tabbed interface for Customer Information and Documents
 * Shows conditional sections based on ticket category
 */
export const TicketEditForm: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const currentTicket = useAppSelector(state => state.ticket.currentTicket);

  // Determine which main content section to show based on category
  const showTicketInformation = currentTicket?.category === 'GEN' || !currentTicket?.category;
  const showHstAccessSection = currentTicket?.category === 'HST';
  const showCpsiAccessSection = currentTicket?.category === 'CPSI';
  const showFacilityMaintenanceSection = currentTicket?.category === 'FAC';
  const showPMDetailsSection = currentTicket?.category === 'PM';
  const showCustomApplicationSection = currentTicket?.category === 'APP';
  const showReportingSection = currentTicket?.category === 'RPT';
  const showCostSection = currentTicket?.category === 'PM' || currentTicket?.category === 'FAC';

  // Build tab configuration dynamically based on category
  const tabs = useMemo(() => {
    const tabList: { label: string; content: React.ReactNode }[] = [
      { label: 'Customer Information', content: <CustomerInformationSection /> },
      { label: 'Documents', content: <DocumentsSection /> },
      { label: 'Assignment & Tracking', content: <Stack spacing={2.5}><AssignmentInformationSection showGuidance={false} /><TimeTrackingSection /></Stack> },
      { label: 'Notes', content: <NotesSection /> },
      { label: 'Workflow', content: <WorkflowApprovalsSection /> },
    ];
    if (showCostSection) {
      tabList.push({ label: 'Cost Tracking', content: <CostSection /> });
    }
    tabList.push({ label: 'Resolution', content: <ResolutionSection /> });
    return tabList;
  }, [showCostSection]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <CardContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
      <form autoComplete="off">
        {/* Main ticket section that appears above tabs */}
        <Stack spacing={2.5} sx={{ mb: 3 }}>
          <CategoryClassificationSection isEditMode={true} />
          
          {/* Conditional Main Content Section */}
          {showTicketInformation && <TicketInformationSection />}
          {showHstAccessSection && <HstAccessSection />}
          {showCpsiAccessSection && <CpsiAccessSection />}
          {showFacilityMaintenanceSection && <FacilityMaintenanceSection mode="ticket" hideEstimatedCost />}
          {showPMDetailsSection && <PMDetailsSection mode="ticket" />}
          {showCustomApplicationSection && <CustomApplicationSection mode="ticket" />}
          {showReportingSection && <ReportingSection mode="ticket" />}
        </Stack>

        {/* Tab Navigation */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="ticket form tabs"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                color: '#64748b',
                '&.Mui-selected': {
                  color: '#162346',
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#3b82f6',
                height: 3
              }
            }}
          >
            {tabs.map((tab, index) => (
              <Tab
                key={tab.label}
                label={tab.label}
                id={`ticket-form-tab-${index}`}
                aria-controls={`ticket-form-tabpanel-${index}`}
              />
            ))}
          </Tabs>
        </Box>

        {/* Tab Content */}
        {tabs.map((tab, index) => (
          <TabPanel key={tab.label} value={activeTab} index={index}>
            {tab.content}
          </TabPanel>
        ))}
      </form>
    </CardContent>
  );
};

export default TicketEditForm;
